'use client';

import { breakpoints, twMerge } from '@shared/themes';
import { ROUTES, useMediaQuery, useRegisterStickyElement } from '@shared/utils';
import { type ComponentProps, useRef } from 'react';
import { PageHeader } from '../PageHeader/PageHeader';
import { PageToolbarContainer } from './components/PageToolbarContainer/PageToolbarContainer';

type PageToolbarProps = ComponentProps<typeof PageToolbarContainer> & {
  title: string;
  breakpoint?: 'md' | 'lg';
};

function PageToolbar({ className, title, children, breakpoint = 'md', ...props }: PageToolbarProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Consumers of this component may control the sticky behaviour via className, so we need to inspect the className to determine when stickyness applies
  const resolvedClassNames = twMerge('sticky', className).split(' ');
  const isAlwaysSticky = resolvedClassNames.includes('sticky');
  const stickyBreakpoint = Object.entries(breakpoints).find(([key]) => resolvedClassNames.includes(`${key}:sticky`))?.[1] ?? 99999;
  const isSticky = useMediaQuery(isAlwaysSticky ? '(width >= 0px)' : `(width >= ${stickyBreakpoint}px)`, { defaultMatches: isAlwaysSticky });
  useRegisterStickyElement(ref, { id: 'page-toolbar', isSticky });

  const breakpointClasses = {
    md: {
      container: 'md:pb-4 md:bg-background',
      wrapper: 'md:ll-container-inner',
      header: 'md:mb-0 md:mx-0 md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2',
    },
    lg: {
      container: 'lg:pb-4 lg:bg-background',
      wrapper: 'lg:ll-container-inner',
      header: 'lg:mb-0 lg:mx-0 lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2',
    },
  };

  const classes = breakpointClasses[breakpoint];

  return (
    <PageToolbarContainer
      data-testid="page-toolbar"
      className={twMerge(`ll-grid-container border-b border-container ${classes.container} sticky z-30 bg-background/95`, className)}
      {...props}
      ref={ref}
    >
      <div className={`ll-container-full ${classes.wrapper} relative`} data-track-container="Sub-navigation">
        <PageHeader title={title} className={`mb-4 mx-5 ${classes.header}`} href={ROUTES.HOME} />
        {children}
      </div>
    </PageToolbarContainer>
  );
}

export { PageToolbar };
